import React from 'react';
import safeToast from '../utils/safeToast';
import { dispatchOwnerTokenEvent } from '../utils/ownerTokenEvents';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (auth: { id: string; username?: string | null; token: string }) => void;
};

// Lightweight sign-in modal with simple CAPTCHA, tailored for Broadcast gating
export const BroadcastLoginModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [captcha, setCaptcha] = React.useState<string>('');
  const [captchaInput, setCaptchaInput] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const randomCode = (len: number) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < len; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const regenerate = React.useCallback(() => {
    setCaptcha(randomCode(5));
    setCaptchaInput('');
  }, []);

  React.useEffect(() => { if (isOpen) regenerate(); }, [isOpen, regenerate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { safeToast('warning', 'Enter email and password'); return; }
    if (!captcha || captchaInput.trim().toUpperCase() !== captcha.trim().toUpperCase()) {
      safeToast('error', 'CAPTCHA incorrect');
      regenerate();
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        safeToast('error', text || 'Login failed');
        return;
      }
      const data = await res.json().catch(() => ({}));
      const token = data?.token || '';
      const id = String(data?.user?.id || data?.user?._id || '');
      const username = data?.user?.username || null;
      if (!id) { safeToast('error', 'Login response incomplete'); return; }
      try {
        localStorage.setItem('owner_token', token);
        dispatchOwnerTokenEvent();
      } catch {}
      try { localStorage.setItem('owner_session', JSON.stringify({ id, username })); } catch {}
      onSuccess({ id, username, token });
      // delay close slightly to give parent a beat to open next modal
      setTimeout(onClose, 50);
    } catch (err: any) {
      safeToast('error', err?.message || 'Login error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative shadow-xl">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">✕</button>
        <h3 className="text-lg font-semibold mb-1">Sign in to Broadcast</h3>
        <p className="text-sm text-gray-600 mb-4">Verify you own this card to access Broadcast.</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
              <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600">
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">CAPTCHA</label>
            <div className="flex items-center gap-2 mb-1">
              <div className="px-3 py-2 rounded bg-gray-100 font-mono tracking-widest select-none">{captcha}</div>
              <button type="button" onClick={regenerate} className="text-xs text-blue-600">Refresh</button>
            </div>
            <input type="text" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} placeholder="Enter the code" className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
          </div>
          <button type="submit" disabled={submitting} className="w-full py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BroadcastLoginModal;
